// shared/document-format.js — the formatting primitives the invoice, quote and
// credit note documents are built from, imported by BOTH runtimes: the PDF
// renderer (server/invoice-pdf.js) and the compose previews (src/js/widgets/*).
//
// Pure: no imports beyond shared/currencies.js, no DOM, no node builtins.
//
// These five functions used to exist twice — once in server/invoice-pdf.js and
// once in src/js/lib/format.js — which is how the compose preview came to format
// dates in the viewer's locale while the PDF used en-GB, and how a date-alignment
// fix landed in the preview (#195) and then again, a day later, in the PDF (#196).
import { isSupportedCurrency } from "./currencies.js";

export function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

// The company's logo as an <img src> (logo_base64). Only a data URL of an
// image type the logo route takes, in base64's own alphabet and nothing after
// it, is drawn; anything else draws no logo. That alphabet holds no quote, no
// angle bracket and no ampersand, so a stored value can never end the
// attribute it sits in, and every caller escapes it as well. This is what
// keeps a logo stored before the route checked the whole value harmless: one
// that carried markup after "base64," was written straight into every PDF,
// which Chromium prints on the API's own host, and into the web's preview.
export const LOGO_DATA_URL_PATTERN = /^data:image\/(png|jpeg|gif|webp|svg\+xml);base64,[A-Za-z0-9+/]+={0,2}$/;

export function logoImageSrc(value) {
  const text = typeof value === "string" ? value : "";
  return LOGO_DATA_URL_PATTERN.test(text) ? text : "";
}

/** The logo's <img>, or "" when there is none that can be drawn. */
export function logoImageHtml(value, { className = "invoice-preview-logo", alt = "" } = {}) {
  const src = logoImageSrc(value);
  return src ? `<img class="${escapeHtml(className)}" src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" />` : "";
}

// Bank sort codes render as 12-34-56, grouped as the digits arrive so a
// half-typed code in the compose form still reads correctly.
export function formatSortCode(rawValue) {
  const digits = String(rawValue || "").replace(/\D/g, "").slice(0, 6);
  if (!digits) return "";
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
}

// Long-form date for the document face: "3 September 2026".
//
// Always en-GB, never the viewer's locale. The document a client receives is
// the PDF, so the compose preview has to render the same string — a US-locale
// browser previewing "September 3, 2026" against a PDF reading "3 September
// 2026" is the preview lying about the document.
// A day on the calendar, with no time and no timezone: "2026-10-01".
const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

export function formatDocumentDate(rawDate) {
  // A date the caller wrote as a calendar day is that day, wherever the reader
  // is. `new Date("2026-10-01")` is UTC midnight, which a device west of UTC
  // prints as 30 September: an instalment due on the 1st, on the document the
  // client receives, dated the day before. The document is read on a phone in
  // any timezone now, so the day is built locally instead of shifted into one.
  const day = typeof rawDate === "string" ? CALENDAR_DAY.exec(rawDate.trim()) : null;
  const parsed = day
    ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]))
    : rawDate instanceof Date ? rawDate : new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
}

// Three letters for every month, September included.
export const SHORT_MONTHS = Object.freeze(["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]);

// Short-form date for lists and rows: "3 Sep", or "3 Sep 2026" with `year`.
//
// The month comes from SHORT_MONTHS and never from a runtime's locale data. For
// en-GB `month: "short"` gives "Sept" in Chrome and Node and "Sep" from Apple's
// Foundation, so the same day read differently on the web and the iPhone. Only
// the day and the month's number are asked of Intl, for the time zone.
export function formatShortDate(rawDate, { year = false, timeZone } = {}) {
  const day = typeof rawDate === "string" ? CALENDAR_DAY.exec(rawDate.trim()) : null;
  const parsed = day
    ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]))
    : rawDate instanceof Date ? rawDate : new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return "";
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "numeric", year: "numeric", ...(timeZone ? { timeZone } : {}) })
      .formatToParts(parsed)
      .map((part) => [part.type, part.value])
  );
  const short = `${Number(parts.day)} ${SHORT_MONTHS[Number(parts.month) - 1]}`;
  return year ? `${short} ${parts.year}` : short;
}

// A month on the calendar, from any day in it: "October 2026". An HMRC period
// rate is a month's (#931), so that is what the document names.
export function formatDocumentMonth(rawDate) {
  const day = typeof rawDate === "string" ? CALENDAR_DAY.exec(rawDate.trim()) : null;
  if (!day) return "";
  const parsed = new Date(Number(day[1]), Number(day[2]) - 1, 1);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("en-GB", { year: "numeric", month: "long" });
}

// The exchange rate a document's VAT was put into sterling at, as the document
// states it (#931): "Exchange rate: £1 = 1.1665 EUR, HMRC’s rate for October
// 2026." The currency by its code, because $ is four of the currencies
// Sendinvo bills in. Plain text: the caller escapes it.
export function exchangeRateSentence({ currency = "", rateText = "", rateDate = "", source = "hmrc" } = {}) {
  const code = String(currency || "").toUpperCase();
  const where = source === "market"
    ? `the UK market rate on ${formatDocumentDate(rateDate)}`
    : `HMRC’s rate for ${formatDocumentMonth(rateDate)}`;
  return `Exchange rate: £1 = ${rateText} ${code}, ${where}.`;
}

// Money for the document face, with the symbol and the digits in separate
// spans so the stylesheet can size them independently.
export function formatCurrencyPreviewHtml(rawValue, currency = "GBP") {
  const amount = Number.parseFloat(String(rawValue ?? 0));
  const safe = Number.isFinite(amount) ? amount : 0;
  const code = isSupportedCurrency(currency) ? String(currency).toUpperCase() : "GBP";
  const parts = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).formatToParts(safe);

  return parts
    .map((part) => {
      const value = escapeHtml(part.value);
      if (part.type === "currency") return `<span class="invoice-preview-currency-symbol">${value}</span>`;
      if (part.type === "integer" || part.type === "group" || part.type === "decimal" || part.type === "fraction") {
        return `<span class="invoice-preview-currency-number">${value}</span>`;
      }
      return value;
    })
    .join("");
}

// Escape a value and turn its newlines into <br /> — addresses, notes, and the
// credit note's reason all arrive as multi-line free text.
export function toPreviewMultiline(value, fallback = "") {
  const text = String(value || "").trim();
  if (!text) return escapeHtml(fallback);
  return escapeHtml(text).replace(/\n/g, "<br />");
}

// The time of supply (tax point) line an invoice carries when it differs from
// the issue date.
//
// UK VAT invoices must state a tax point, and it is only the same as the issue
// date when the invoice goes out within 14 days of the work. Beyond that — or
// for work spanning a period — it has to be stated in its own right.
//
// One date or two is the same question asked twice: a job done in a day has a
// supply date, a job done over a fortnight has a supply period. Equal dates
// collapse to the singular rather than printing the same day twice.
export function formatSupplyPeriod(from, to) {
  const start = String(from || "").trim();
  const end = String(to || "").trim();
  if (!start && !end) return "";
  if (!start || start === end) {
    return `Supply date ${formatDocumentDate(end || start)}`;
  }
  if (!end) return `Supply date ${formatDocumentDate(start)}`;
  return `Supply period ${formatDocumentDate(start)} – ${formatDocumentDate(end)}`;
}
